"use client";

import {
  BookOpen,
  CircleUserRound,
  Info,
  LogOut,
  ScrollText,
  Shield,
  Ticket,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shadcn/ui/dropdown-menu";
import {
  HamburgerMenuIcon,
  MagnifyingGlassIcon,
} from "@/components/common/icons";
import {
  DiscoverEntrySource,
  HeaderEvents,
  SearchEvents,
  SearchSurface,
  UserEvents,
  resetAnalytics,
  trackEvent,
} from "@punchup/analytics";
import { Popover, PopoverContent, PopoverTrigger } from "@/shadcn/ui/popover";
import { cn } from "@punchup/utils";
import {
  headerHamburgerButtonStyles,
  headerLinkStyles,
} from "@punchup/ui-common/components/header/styles";
import { startTransition, useEffect, useState } from "react";
import { useFeatureGate } from "statsig-react";
import { useLastActiveAt, useSession } from "@/hooks";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { Database } from "@punchup/data/supabase.types";
import HeaderNavigationMenuItem from "@punchup/ui-common/components/header/HeaderNavigationMenuItem";
import Image from "next/image";
import Link from "next/link";
import { NotificationsBell } from "../notifications/NotificationsBell";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "dayjs";
import logoWithIcon from "@/public/resources/punchupLogoIcon.svg";
import punchupIcon from "@/public/resources/punchupicon.svg";
import { useIsDesktop } from "@punchup/utils";
import { usePageScroll } from "@/components/providers/PageScrollProvider";
import { useSearchOverlay } from "@/components/search/SearchOverlayContext";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export default function Header({ showMenus = true }: { showMenus?: boolean }) {
  const supabase = createClientComponentClient<Database>();
  const router = useRouter();
  const session = useSession();
  const { gate } = useFeatureGate("new_user_onboarding_flow");
  const showNewOnboarding = gate.value === true;
  useLastActiveAt();
  const { open: openSearch } = useSearchOverlay();
  const { gate: searchGate } = useFeatureGate("search_discover_redesign");
  const isSearchRedesignEnabled = searchGate.value === true;
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { scrollTop, scrollDirection } = usePageScroll();
  const isDesktop = useIsDesktop();

  const loading = session === undefined;
  const isLoggedIn = session !== undefined && session !== null;

  // Search-entry analytics: one helper so every header search affordance emits the same
  // shape (surface + device + where the click leads), making the search → discover funnel
  // reconstructable in Mixpanel. See docs/specs/mixpanel.md.
  const trackHeaderSearchClick = (
    destination: "search_overlay" | "discover_page" | "search_page"
  ) => {
    trackEvent(
      isLoggedIn
        ? SearchEvents.CLICKED_SEARCH_ON_HEADER_LOGGED_IN
        : SearchEvents.CLICKED_SEARCH_ON_HEADER_LOGGED_OUT,
      {
        source: SearchSurface.HEADER,
        device: isDesktop ? "desktop" : "mobile",
        destination,
      }
    );
  };

  // The redesigned header search icon: desktop opens the overlay, mobile jumps straight to
  // /discover (tagged so the discover page-view event can attribute the entry path).
  const handleRedesignedSearchClick = () => {
    if (isDesktop) {
      trackHeaderSearchClick("search_overlay");
      openSearch();
    } else {
      trackHeaderSearchClick("discover_page");
      router.push(`/discover?source=${DiscoverEntrySource.HEADER_SEARCH}`);
    }
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) {
      console.error("Error signing out:", error);
    } else {
      trackEvent(UserEvents.LOGGED_OUT);
      resetAnalytics();
      if (searchParams.get("showWelcomeBanner")) {
        router.push(window.location.pathname);
      }
    }

    startTransition(() => {
      // Refresh the current route and fetch new data from the server without
      // losing client-side browser or React state.
      router.refresh();
    });
  };

  const homeLinkHref = "/";

  const menuIconClassName = "w-5 h-5";

  const SignedOutMenu = () => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Hamburger Menu"
            className={cn(headerLinkStyles, headerHamburgerButtonStyles)}
            onClick={() => {
              trackEvent(UserEvents.CLICKED_MENU_DROPDOWN);
            }}
          >
            <HamburgerMenuIcon className="w-[28px] h-[28px] stroke-primary-blue" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="p-0 w-52 overflow-clip">
          <DropdownMenuItem asChild>
            <Link
              href="https://ticketing.punchup.live"
              className="flex items-center w-full gap-2 cursor-pointer"
            >
              <Ticket className={menuIconClassName} />
              Ticketing
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              href="https://blog.punchup.live"
              target="_blank"
              className="flex items-center w-full gap-2 cursor-pointer"
            >
              <BookOpen className={menuIconClassName} />
              Blog
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              href="/privacy"
              className="flex items-center w-full gap-2 cursor-pointer"
            >
              <Shield className={menuIconClassName} />
              Privacy Policy
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              href="/tos"
              className="flex items-center w-full gap-2 cursor-pointer"
            >
              <ScrollText className={menuIconClassName} />
              Terms of Service
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              href="/about"
              className="flex items-center w-full gap-2 cursor-pointer"
            >
              <Info className={menuIconClassName} />
              About Us
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const handleSignInClick = () => {
    trackEvent(HeaderEvents.CLICKED_SIGN_IN);

    // If user exists and is newly created, send to onboarding
    if (session?.user) {
      const userCreatedAt = dayjs(session.user.created_at).utc();
      const now = dayjs().utc();
      const timeSinceCreation = now.diff(userCreatedAt, "seconds");

      // Only show new onboarding if feature gate is enabled and user is new
      if (showNewOnboarding && timeSinceCreation < 300) {
        router.push("/suggested");

        return;
      }
    }

    // Otherwise proceed to normal signin
    router.push(`/signin?redirectRoute=${pathName}`);
  };

  return (
    <header
      className={cn(
        "h-header-bar fixed left-0 right-0 w-full z-50 bg-ivory border-b border-[var(--punchup-color-zinc-200)] transition-all duration-200 ease-in-out-cubic",
        isDesktop || scrollTop < 15 || scrollDirection === "up"
          ? "top-app-banner"
          : "-top-[var(--header-bar-height)]"
      )}
    >
      <nav
        className="h-header-bar flex flex-row items-center justify-between w-full px-4 md:px-5 py-1.5 md:py-3"
        aria-label="Main site navigation"
      >
        <div className="flex flex-row items-center justify-start gap-1 md:gap-2">
          {showMenus ? (
            <div
              onClick={() => {
                trackEvent(HeaderEvents.CLICKED_HEADER_LOGO);
                trackEvent(HeaderEvents.NAVIGATE_TO_FEED_FROM_HOME);
              }}
            >
              <Link aria-label="Home" href={homeLinkHref}>
                {/* show the logo with icon on desktop */}
                <Image
                  src={logoWithIcon}
                  aria-label="Punchup logo"
                  alt="Punchup logo"
                  width={118}
                  height={20}
                  className="hidden lg:block h-[20px] md:h-[24px] w-auto"
                />
                {/* show just the icon on mobile */}
                <Image
                  src={punchupIcon}
                  aria-label="Punchup icon"
                  alt="Punchup icon"
                  width={30}
                  height={15}
                  className="lg:hidden"
                />
              </Link>
            </div>
          ) : (
            <div
              onClick={() => {
                trackEvent(HeaderEvents.CLICKED_HEADER_LOGO);
                trackEvent(HeaderEvents.NAVIGATE_TO_FEED_FROM_HOME);
              }}
            >
              <Link
                aria-label="Home"
                href={homeLinkHref}
                onClick={(e) => {
                  if (
                    !confirm(
                      "Are you sure you want to leave this page and go back to the home page?"
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
              >
                <Image
                  src={logoWithIcon}
                  aria-label="Punchup logo"
                  alt="Punchup logo"
                  width={118}
                  height={20}
                  className="h-[20px] md:h-[24px] w-auto"
                />
              </Link>
            </div>
          )}
        </div>
        <>
          {!loading && showMenus && (
            <>
              {isLoggedIn ? (
                <div className="flex items-center justify-center gap-x-1 md:gap-x-3">
                  {isSearchRedesignEnabled ? (
                    <button
                      type="button"
                      aria-label="Search"
                      className={headerLinkStyles}
                      onClick={handleRedesignedSearchClick}
                    >
                      <MagnifyingGlassIcon
                        width={24}
                        height={24}
                        className="stroke-primary-blue"
                      />
                    </button>
                  ) : (
                    <Link
                      href="/search"
                      aria-label="Search"
                      className={headerLinkStyles}
                      onClick={() => trackHeaderSearchClick("search_page")}
                    >
                      <MagnifyingGlassIcon
                        width={24}
                        height={24}
                        className="stroke-primary-blue"
                      />
                    </Link>
                  )}
                  <NotificationsBell />
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        aria-label="Hamburger Menu"
                        className={cn(
                          headerLinkStyles,
                          headerHamburgerButtonStyles
                        )}
                        onClick={() => {
                          trackEvent(UserEvents.CLICKED_MENU_DROPDOWN);
                        }}
                      >
                        <HamburgerMenuIcon className="w-[28px] h-[28px] stroke-primary-blue" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="p-0 w-52 overflow-clip"
                      align="end"
                      sideOffset={6}
                    >
                      <ul>
                        <HeaderNavigationMenuItem
                          href="/profile"
                          linkAriaLabel="Profile"
                          icon={
                            <CircleUserRound className={menuIconClassName} />
                          }
                          onClick={() => {
                            trackEvent(
                              UserEvents.NAVIGATE_TO_PROFILE_FROM_DROPDOWN
                            );
                          }}
                        >
                          Profile
                        </HeaderNavigationMenuItem>
                        <HeaderNavigationMenuItem
                          icon={<LogOut className={menuIconClassName} />}
                          linkAriaLabel="Sign out"
                          onClick={handleSignOut}
                        >
                          Sign out
                        </HeaderNavigationMenuItem>
                        <div className="h-px my-1 bg-neutral-200" />
                        <HeaderNavigationMenuItem
                          href="https://ticketing.punchup.live"
                          linkAriaLabel="Ticketing"
                          icon={<Ticket className={menuIconClassName} />}
                          onClick={() => {
                            trackEvent(HeaderEvents.CLICKED_TIXOLOGI);
                          }}
                        >
                          Ticketing
                        </HeaderNavigationMenuItem>
                        <HeaderNavigationMenuItem
                          href="https://blog.punchup.live"
                          linkAriaLabel="Blog"
                          icon={<BookOpen className={menuIconClassName} />}
                          onClick={() => {
                            trackEvent(HeaderEvents.CLICKED_BLOG);
                          }}
                        >
                          Blog
                        </HeaderNavigationMenuItem>

                        <HeaderNavigationMenuItem
                          href="/privacy"
                          linkAriaLabel="Privacy Policy"
                          icon={<Shield className={menuIconClassName} />}
                          onClick={() => {
                            trackEvent(HeaderEvents.CLICKED_PRIVACY_POLICY);
                          }}
                        >
                          Privacy Policy
                        </HeaderNavigationMenuItem>
                        <HeaderNavigationMenuItem
                          href="/tos"
                          linkAriaLabel="Terms of Service"
                          icon={<ScrollText className={menuIconClassName} />}
                          onClick={() => {
                            trackEvent(HeaderEvents.CLICKED_TERMS_OF_SERVICE);
                          }}
                        >
                          Terms of Service
                        </HeaderNavigationMenuItem>
                        <HeaderNavigationMenuItem
                          href="/about"
                          linkAriaLabel="About Us"
                          icon={<Info className={menuIconClassName} />}
                          onClick={() => {
                            trackEvent(HeaderEvents.CLICKED_ABOUT_US);
                          }}
                        >
                          About Us
                        </HeaderNavigationMenuItem>
                      </ul>
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-x-1 md:gap-x-3">
                  {isSearchRedesignEnabled ? (
                    <button
                      type="button"
                      aria-label="Search"
                      className={headerLinkStyles}
                      onClick={handleRedesignedSearchClick}
                    >
                      <MagnifyingGlassIcon
                        width={24}
                        height={24}
                        className="stroke-primary-blue"
                      />
                    </button>
                  ) : (
                    <Link
                      href="/search"
                      aria-label="Search"
                      className={headerLinkStyles}
                      onClick={() => trackHeaderSearchClick("search_page")}
                    >
                      <MagnifyingGlassIcon
                        width={24}
                        height={24}
                        className="stroke-primary-blue"
                      />
                    </Link>
                  )}
                  <Link
                    href={`/signin?redirectRoute=${pathName}`}
                    aria-label="Sign in"
                    className={cn(
                      headerLinkStyles,
                      "md:text-base text-sm font-semibold"
                    )}
                    onClick={handleSignInClick}
                  >
                    Sign in
                  </Link>
                  {!session && <SignedOutMenu />}
                </div>
              )}
            </>
          )}
        </>
      </nav>
    </header>
  );
}
